import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { of } from 'rxjs';

import { NotificationType } from 'application/notification/model/notification.type';
import { DocumentRepositoryPort } from 'document/port/document-repository.port';
import { INotification, Notification } from 'domain/notification/notification';
import { NotificationMeeting } from 'domain/notification/notification-meeting';
import { ModuleRepositoryPort } from 'module/port/module-repository.port';
import { TaskRepositoryPort } from 'task/port/task-repository.port';
import { UserRepositoryPort } from 'user/port/user-repository.port';

import { AppLocator } from '../../app.locator';

export interface NotificationMeetingDocument {
	readonly coachId: string;
	readonly startDate: number;
	readonly endDate: number;
}

export interface NotificationDocument {
	readonly id: string;
	readonly type: string;
	readonly taskId: string;
	readonly creation: number;
	readonly moduleId: string;
	readonly documentId: string;
	readonly sourceUserId: string;
	readonly targetUserId: string;
	readonly contactsName: string[];
	readonly updateTimestamp: number;
	readonly meeting: NotificationMeetingDocument;
}

export type NotificationState = EntityState<NotificationDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'notifications' })
export class NotificationStore extends EntityStore<NotificationState> {
	constructor() {
		super();
	}

	public get _userRepositoryPort(): UserRepositoryPort {
		return AppLocator.inject<UserRepositoryPort>(UserRepositoryPort);
	}

	public get _taskRepositoryPort(): TaskRepositoryPort {
		return AppLocator.inject<TaskRepositoryPort>(TaskRepositoryPort);
	}

	public get _documentRepositoryPort(): DocumentRepositoryPort {
		return AppLocator.inject<DocumentRepositoryPort>(DocumentRepositoryPort);
	}

	public get _moduleRepositoryPort(): ModuleRepositoryPort {
		return AppLocator.inject<ModuleRepositoryPort>(ModuleRepositoryPort);
	}

	public asDocument(notification: INotification): NotificationDocument {
		return notification
			? {
					id: notification.id,
					type: notification.type,
					taskId: notification.taskId,
					moduleId: notification.moduleId,
					documentId: notification.documentId,
					sourceUserId: notification.sourceUserId,
					targetUserId: notification.targetUserId,
					creation: notification.creation.getTime(),
					contactsName: notification.contactsName || [],
					updateTimestamp: notification.updateTimestamp,
					meeting: notification.meeting
						? {
								startDate: notification.meeting.startDate.getTime(),
								endDate: notification.meeting.endDate.getTime(),
								coachId: notification.meeting.coachId,
						  }
						: null,
			  }
			: null;
	}

	public asEntity(document: NotificationDocument): Notification {
		return !!document
			? new Notification(
					document.id,
					document.taskId,
					new Date(document.creation),
					document.moduleId,
					document.documentId,
					document.sourceUserId,
					document.targetUserId,
					NotificationType[document.type],
					document.contactsName || [],
					document.updateTimestamp,
					document.meeting
						? new NotificationMeeting(
								document.meeting.coachId,
								new Date(document.meeting.startDate),
								new Date(document.meeting.endDate),
								document.meeting.coachId
									? this._userRepositoryPort.observeUserById(document.meeting.coachId)
									: of(null)
						  )
						: null,
					document.taskId
						? this._taskRepositoryPort.observeTask(document.taskId)
						: of(null),
					document.moduleId
						? this._moduleRepositoryPort.observeModuleById(document.moduleId)
						: of(null),
					document.sourceUserId
						? this._userRepositoryPort.observeUserById(document.sourceUserId)
						: of(null),
					document.targetUserId
						? this._userRepositoryPort.observeUserById(document.targetUserId)
						: of(null),
					document.documentId
						? this._documentRepositoryPort.observeDocument(document.documentId)
						: of(null)
			  )
			: null;
	}
}
